import { FormattedMessage, useMelioIntl } from '@melio/ar-domain';
import {
  ActionsDropdownMenu,
  Button,
  Container,
  Form,
  Group,
  Icon,
  Text,
  useFormSubmissionController,
  useMelioForm,
} from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';
import React, { useState } from 'react';
import { object, SchemaOf, string } from 'yup';

import { AddBankFundingSourceFormFields } from '../types';

type AddBankFundingSourceFormProps = {
  onSubmit: (data: AddBankFundingSourceFormFields) => void;
  isSaving: boolean;
  fundingSourceName?: string;
  amount: number;
  onDeleteBankAccount: VoidFunction;
};

const useSchema = () => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    email: string()
      .email()
      .trim()
      .required(formatMessage('ar.dashboard.activities.cardHolder.form.fields.email.required.text')),
  }) as SchemaOf<AddBankFundingSourceFormFields>;
};

export const AddBankFundingSourceForm = forwardRef<AddBankFundingSourceFormProps>(
  ({ onDeleteBankAccount, fundingSourceName, onSubmit, isSaving, amount }, ref) => {
    const { onSubmissionStateChange, submitButtonProps } =
      useFormSubmissionController<AddBankFundingSourceFormFields>();
    const { formProps, registerField } = useMelioForm<AddBankFundingSourceFormFields>({
      onSubmit,
      schema: useSchema(),
      isSaving,
      onSubmissionStateChange,
    });
    const { formatCurrency, formatMessage } = useMelioIntl();
    const [isActionsMenuOpen, setActionsMenuOpen] = useState(false);

    return (
      <Group variant="vertical" spacing="l" ref={ref}>
        <Group variant="vertical" spacing="m">
          <Group variant="vertical" spacing="xxs">
            <Text textStyle="body2Semi">
              <FormattedMessage id="ar.newGuestPayment.payByBank.form.title.text" />
            </Text>
            <Text textStyle="body3">
              <FormattedMessage id="ar.newGuestPayment.payByBank.form.description.text" />
            </Text>
          </Group>
          <Form data-component="AddBankFundingSourceForm" {...formProps} columns={2}>
            <Form.TextField
              colSpan={2}
              labelProps={{ label: formatMessage('ar.dashboard.activities.cardHolder.form.fields.email.label') }}
              {...registerField('email')}
            />
          </Form>
          {fundingSourceName ? (
            <Container border="regular" paddingX="m" paddingY="m">
              <Group justifyContent="space-between">
                <Group alignItems="center">
                  <Icon type="bank" />
                  <Group spacing="none" variant="vertical">
                    <Text textStyle="body4" color="neutral.darker">
                      <FormattedMessage id="ar.newGuestPayment.payByBank.accountCard.subTitle.text" />
                    </Text>
                    <Text textStyle="body2Semi">{fundingSourceName}</Text>
                  </Group>
                </Group>
                <ActionsDropdownMenu
                  size="small"
                  items={[
                    {
                      label: formatMessage('ar.newGuestPayment.payByBank.accountCard.actions.delete.text'),
                      onClick: onDeleteBankAccount,
                      variant: 'critical',
                    },
                  ]}
                  isOpen={isActionsMenuOpen}
                  onOpenChange={setActionsMenuOpen}
                />
              </Group>
            </Container>
          ) : null}
        </Group>
        <Button
          data-testid="add-bank-submit-button"
          size="large"
          label={
            fundingSourceName
              ? formatMessage('ar.dashboard.activities.cardHolder.form.buttons.submit.text', {
                  amount: formatCurrency(amount),
                })
              : formatMessage('ar.newGuestPayment.payByBank.form.button.submit.text')
          }
          {...submitButtonProps}
        />
      </Group>
    );
  }
);
AddBankFundingSourceForm.displayName = 'AddBankFundingSourceForm';
